import { hostBridge } from '@publicala/delfino';
import type { PlayTrackPayload, SeekToPositionPayload } from '@publicala/delfino';
import type { VueEventBus, VuexStore } from '../types';
import audioEvents from '../../components/partials/Audiobook/audioEvents.js';

let playbackEventBus: VueEventBus | null = null;
let playbackStore: VuexStore | null = null;

/**
 * Configures the dependencies needed for PlaybackCommands.
 * Must be called before registerPlaybackCommands.
 *
 * @param eventBus - Vue eventBus for emitting events
 * @param store - Vuex store to check state
 */
export function configurePlaybackCommands(eventBus: VueEventBus, store: VuexStore): void {
    playbackEventBus = eventBus;
    playbackStore = store;
}

/**
 * Registers the PlaybackCommands that Volpe implements.
 * The Host (Farfalla/Fenice) calls these methods to control the audio player.
 */
export function registerPlaybackCommands(): void {
    if (!playbackEventBus || !playbackStore) {
        console.warn('[PlaybackCommands] eventBus or store not configured. Call configurePlaybackCommands first.');
        return;
    }

    const eventBus = playbackEventBus;
    const store = playbackStore;

    hostBridge.registerPlaybackCommands({
        /**
         * External command to play a track.
         *
         * @param payload - Contains optional trackIndex (0-based)
         *   - If trackIndex is provided: switches to that track and plays
         *   - If trackIndex is omitted: plays/resumes the current track
         */
        externalPlayTrack: async (payload: PlayTrackPayload): Promise<void> => {
            if (store.getters.isAudiobook) {
                eventBus.$emit(audioEvents.IN_EVENTS.PLAY, payload.trackIndex);
            }
        },

        /**
         * External command to pause playback.
         */
        externalPauseTrack: async (): Promise<void> => {
            if (store.getters.isAudiobook) {
                eventBus.$emit(audioEvents.IN_EVENTS.PAUSE);
            }
        },

        /**
         * External command to go to the next track.
         */
        externalNextTrack: async (): Promise<void> => {
            if (store.getters.isAudiobook) {
                eventBus.$emit(audioEvents.IN_EVENTS.NEXT);
            }
        },

        /**
         * External command to go to the previous track.
         */
        externalPreviousTrack: async (): Promise<void> => {
            if (store.getters.isAudiobook) {
                eventBus.$emit(audioEvents.IN_EVENTS.PREVIOUS);
            }
        },

        /**
         * External command to seek to a specific position.
         *
         * @param payload - Contains newSeekPositionInSeconds
         */
        externalSeekToPosition: async (payload: SeekToPositionPayload): Promise<void> => {
            if (store.getters.isAudiobook) {
                eventBus.$emit(audioEvents.IN_EVENTS.SEEK, payload.newSeekPositionInSeconds);
            }
        },
    });
}
